import { useState, useEffect, useRef } from "react";
import { Upload, Loader2, CheckCircle, AlertCircle, X, FileText, Eye, Download, Trash2, Building2, Calendar, Edit2 } from "lucide-react";
import { adminService, Client, Unidade, TipoDocumento } from "../../../services/adminService";
import { documentService, Document } from "../../../services/documentService";
import { formatDateFromDB, extractDateForInput } from "../../../utils/dateUtils";

export function DocumentUpload() {
  const [clients, setClients] = useState<Client[]>([]);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [faturas, setFaturas] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    clientId: "", unidadeId: "", tipoDocumentoId: "", dataReferencia: "", nome: "", faturaId: ""
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Estados para listagem de documentos
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClientForList, setSelectedClientForList] = useState("");
  
  // Estados para filtros da listagem
  const [filterTipoId, setFilterTipoId] = useState("");
  const [filterUnidadeId, setFilterUnidadeId] = useState("");
  const [filterDataInicio, setFilterDataInicio] = useState("");
  const [filterDataFim, setFilterDataFim] = useState("");
  const [listUnidades, setListUnidades] = useState<Unidade[]>([]);
  
  // Estados para modal de edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [editForm, setEditForm] = useState({ nome: "", tipoDocumentoId: "", unidadeId: "", dataReferencia: "" });
  const [editUnidades, setEditUnidades] = useState<Unidade[]>([]);

  useEffect(() => {
    loadClients();
    loadTiposDocumento();
  }, []);

  useEffect(() => {
    if (formData.clientId) {
      loadUnidades(formData.clientId);
      loadFaturas(formData.clientId);
    } else {
      setUnidades([]);
      setFaturas([]);
    }
  }, [formData.clientId]);

  // Carregar unidades quando cliente da listagem mudar
  useEffect(() => {
    if (selectedClientForList) {
      loadListUnidades(selectedClientForList);
    } else {
      setListUnidades([]);
    }
    // Sempre carregar documentos, com ou sem cliente selecionado
    loadDocuments();
  }, [selectedClientForList]);

  // Carregar documentos quando filtros mudarem
  useEffect(() => {
    loadDocuments();
  }, [currentPage, filterTipoId, filterUnidadeId, filterDataInicio, filterDataFim]);

  const loadListUnidades = async (clientId: string) => {
    try {
      const result = await adminService.listUnidades(clientId);
      setListUnidades(result);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadDocuments = async () => {
    setIsLoadingDocs(true);
    try {
      const result = await adminService.listDocumentsByClient(
        selectedClientForList || '', // Se vazio, backend deve retornar todos
        { 
          page: currentPage, 
          limit: 10,
          tipoDocumentoId: filterTipoId ? parseInt(filterTipoId) : undefined,
          unidadeId: filterUnidadeId ? parseInt(filterUnidadeId) : undefined,
          dataInicio: filterDataInicio || undefined,
          dataFim: filterDataFim || undefined,
        }
      );
      setDocuments(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const clearFilters = () => {
    setFilterTipoId("");
    setFilterUnidadeId("");
    setFilterDataInicio("");
    setFilterDataFim("");
    setCurrentPage(1);
  };

  const handleDeleteDocument = async (id: string) => {
    if (!confirm("Deseja excluir este documento?")) return;
    try {
      await adminService.deleteDocument(id);
      setMessage({ type: "success", text: "Documento excluído!" });
      loadDocuments();
    } catch {
      setMessage({ type: "error", text: "Erro ao excluir documento" });
    }
  };

  const handleEditDocument = async (doc: Document) => {
    setEditingDoc(doc);
    // Carregar unidades do cliente do documento
    try {
      const unidadesResult = await adminService.listUnidades(selectedClientForList);
      setEditUnidades(unidadesResult);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
    setEditForm({
      nome: doc.nome,
      tipoDocumentoId: doc.tipoDocumento.id.toString(),
      unidadeId: doc.unidade?.id?.toString() || "",
      dataReferencia: extractDateForInput(doc.dataReferencia),
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDoc) return;
    try {
      await adminService.updateDocument(editingDoc.id, {
        nome: editForm.nome,
        tipoDocumentoId: parseInt(editForm.tipoDocumentoId),
        unidadeId: editForm.unidadeId ? parseInt(editForm.unidadeId) : null,
        dataReferencia: editForm.dataReferencia,
      });
      setMessage({ type: "success", text: "Documento atualizado!" });
      setShowEditModal(false);
      setEditingDoc(null);
      loadDocuments();
    } catch {
      setMessage({ type: "error", text: "Erro ao atualizar documento" });
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingDoc(null);
  };

  const loadClients = async () => {
    try {
      const result = await adminService.listClients();
      setClients(result.data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    }
  };

  const loadUnidades = async (clientId: string) => {
    try {
      const result = await adminService.listUnidades(clientId);
      setUnidades(result);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadFaturas = async (clientId: string) => {
    try {
      const result = await adminService.listFaturas({ clientId, page: 1 });
      setFaturas(result.data);
    } catch (error) {
      console.error("Erro ao carregar faturas:", error);
    }
  };

  const loadTiposDocumento = async () => {
    try {
      const result = await adminService.listTiposDocumento();
      setTiposDocumento(result.filter(t => t.active));
    } catch (error) {
      console.error("Erro ao carregar tipos:", error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setMessage({ type: "error", text: "Apenas arquivos PDF são permitidos" });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: "error", text: "Arquivo deve ter no máximo 10MB" });
        return;
      }
      setSelectedFile(file);
      if (!formData.nome) {
        setFormData((prev) => ({ ...prev, nome: file.name.replace(".pdf", "") }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !formData.clientId || !formData.tipoDocumentoId || !formData.dataReferencia) {
      setMessage({ type: "error", text: "Preencha todos os campos obrigatórios" });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const data = new FormData();
      data.append("file", selectedFile);
      data.append("clientId", formData.clientId);
      data.append("tipoDocumentoId", formData.tipoDocumentoId);
      data.append("dataReferencia", formData.dataReferencia);
      if (formData.unidadeId) data.append("unidadeId", formData.unidadeId);
      if (formData.nome) data.append("nome", formData.nome);
      if (formData.faturaId) data.append("faturaId", formData.faturaId);

      await adminService.uploadDocument(data);
      setMessage({ type: "success", text: "Documento enviado com sucesso!" });
      setSelectedFile(null);
      setFormData({ clientId: "", unidadeId: "", tipoDocumentoId: "", dataReferencia: "", nome: "", faturaId: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao enviar documento" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-foreground mb-4">Upload de Documento</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Cliente *</label>
            <select value={formData.clientId} onChange={(e) => setFormData((prev) => ({ ...prev, clientId: e.target.value, unidadeId: "" }))}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" required>
              <option value="">Selecione um cliente</option>
              {clients.map((client) => (<option key={client.id} value={client.id}>{client.razaoSocial}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Unidade</label>
            <select value={formData.unidadeId} onChange={(e) => setFormData((prev) => ({ ...prev, unidadeId: e.target.value }))}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground disabled:bg-muted" disabled={!formData.clientId}>
              <option value="">Matriz</option>
              {unidades.map((unidade) => (<option key={unidade.id} value={unidade.id}>{unidade.nome}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Tipo de Documento *</label>
            <select value={formData.tipoDocumentoId} onChange={(e) => setFormData((prev) => ({ ...prev, tipoDocumentoId: e.target.value }))}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" required>
              <option value="">Selecione um tipo</option>
              {tiposDocumento.map((tipo) => (<option key={tipo.id} value={tipo.id}>{tipo.nome}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Vincular à Fatura
              <span className="text-xs text-muted-foreground ml-1">(opcional)</span>
            </label>
            <select 
              value={formData.faturaId} 
              onChange={(e) => setFormData((prev) => ({ ...prev, faturaId: e.target.value }))}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground disabled:bg-muted disabled:cursor-not-allowed" 
              disabled={!formData.clientId || faturas.length === 0}
            >
              <option value="">Sem vínculo</option>
              {faturas.map((fatura) => (
                <option key={fatura.id} value={fatura.id}>
                  Fatura #{fatura.numero} - R$ {Number(fatura.valorTotal).toFixed(2)} - {fatura.status}
                </option>
              ))}
            </select>
            {formData.clientId && faturas.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">Nenhuma fatura disponível para este cliente</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Data de Referência *</label>
            <input type="date" value={formData.dataReferencia} onChange={(e) => setFormData((prev) => ({ ...prev, dataReferencia: e.target.value }))}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" required />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-1">Nome do Documento</label>
            <input type="text" value={formData.nome} onChange={(e) => setFormData((prev) => ({ ...prev, nome: e.target.value }))}
              placeholder="Ex: Laudo PMOC Janeiro 2024" className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground" />
          </div>
        </div>

        <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${selectedFile ? "border-green-400 bg-green-500/10" : "border-input hover:border-brand/50 hover:bg-brand/5"}`}>
          {selectedFile ? (
            <div className="flex items-center justify-center gap-3">
              <Upload className="w-8 h-8 text-green-500" />
              <p className="text-green-600 dark:text-green-400 font-medium">{selectedFile.name}</p>
              <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="p-1 text-red-500 hover:bg-red-500/10 rounded-full" title="Remover arquivo">
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div onClick={() => fileInputRef.current?.click()} className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="font-medium text-foreground">Clique para selecionar o arquivo</p>
              <p className="text-sm text-muted-foreground">Apenas PDF, máximo 10MB</p>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />

        <div className="flex justify-end">
          <button type="submit" disabled={isLoading || !selectedFile}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white rounded-[8px] shadow-[0_4px_12px_-2px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_18px_-4px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 transition-all duration-200 disabled:from-muted disabled:to-muted disabled:text-muted-foreground disabled:shadow-none disabled:cursor-not-allowed disabled:pointer-events-none">
            {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Upload className="w-4 h-4" /> Enviar Documento</>}
          </button>
        </div>
      </form>

      {/* Listagem de Documentos */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Documentos Publicados</h3>
        
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4 p-4 bg-muted/50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Cliente
            </label>
            <select 
              value={selectedClientForList} 
              onChange={(e) => { setSelectedClientForList(e.target.value); setCurrentPage(1); setFilterUnidadeId(""); }}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground"
            >
              <option value="">Todos os clientes</option>
              {clients.map((client) => (<option key={client.id} value={client.id}>{client.razaoSocial}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
            <select 
              value={filterTipoId} 
              onChange={(e) => { setFilterTipoId(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground"
              disabled={!selectedClientForList}
            >
              <option value="">Todos</option>
              {tiposDocumento.map((tipo) => (<option key={tipo.id} value={tipo.id}>{tipo.nome}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Unidade</label>
            <select 
              value={filterUnidadeId} 
              onChange={(e) => { setFilterUnidadeId(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground"
              disabled={!selectedClientForList}
            >
              <option value="">Todas</option>
              <option value="0">Matriz</option>
              {listUnidades.map((unidade) => (<option key={unidade.id} value={unidade.id}>{unidade.nome}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Data Início</label>
            <input 
              type="date" 
              value={filterDataInicio} 
              onChange={(e) => { setFilterDataInicio(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground"
              disabled={!selectedClientForList}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Data Fim</label>
            <input 
              type="date" 
              value={filterDataFim} 
              onChange={(e) => { setFilterDataFim(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground"
              disabled={!selectedClientForList}
            />
          </div>
        </div>
        {(filterTipoId || filterUnidadeId || filterDataInicio || filterDataFim) && (
          <div className="mb-4">
            <button onClick={clearFilters} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              <X className="w-4 h-4" /> Limpar filtros
            </button>
          </div>
        )}

        {!selectedClientForList ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="mb-2">Exibindo todos os documentos de todos os clientes</p>
            <p className="text-xs">Selecione um cliente acima para filtrar</p>
          </div>
        ) : null}

        {isLoadingDocs ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-brand animate-spin" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>
              {selectedClientForList 
                ? 'Nenhum documento encontrado para este cliente' 
                : 'Nenhum documento cadastrado no sistema'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Documento</th>
                    {!selectedClientForList && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Cliente</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Unidade</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Referência</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Publicado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span className="font-medium text-foreground">{doc.nome}</span>
                        </div>
                      </td>
                      {!selectedClientForList && (
                        <td className="px-4 py-3 text-muted-foreground text-sm">
                          {doc.client?.razaoSocial || '—'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-muted-foreground text-sm">{doc.tipoDocumento.nome}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
                          <Building2 className="w-3 h-3" />
                          {doc.unidade?.nome || "Matriz"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
                          <Calendar className="w-3 h-3" />
                          {formatDateFromDB(doc.dataReferencia)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-sm">
                        {formatDateFromDB(doc.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => documentService.viewFile(doc.id)}
                            className="p-2 text-blue-600 hover:bg-blue-500/10 rounded-lg" title="Visualizar">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => documentService.downloadFile(doc.id, doc.arquivo)}
                            className="p-2 text-muted-foreground hover:bg-muted rounded-lg" title="Download">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEditDocument(doc)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg" title="Editar">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteDocument(doc.id)}
                            className="p-2 text-red-600 hover:bg-red-500/10 rounded-lg" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-muted text-foreground rounded hover:bg-muted disabled:opacity-50">
                  Anterior
                </button>
                <span className="text-sm text-muted-foreground">Página {currentPage} de {totalPages}</span>
                <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-muted text-foreground rounded hover:bg-muted disabled:opacity-50">
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Edição */}
      {showEditModal && editingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Editar Documento</h3>
              <button onClick={closeEditModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome do Documento *</label>
                <input type="text" value={editForm.nome} onChange={(e) => setEditForm(p => ({ ...p, nome: e.target.value }))} required 
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tipo de Documento *</label>
                <select value={editForm.tipoDocumentoId} onChange={(e) => setEditForm(p => ({ ...p, tipoDocumentoId: e.target.value }))} required
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground">
                  <option value="">Selecione um tipo</option>
                  {tiposDocumento.map((tipo) => (<option key={tipo.id} value={tipo.id}>{tipo.nome}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Unidade</label>
                <select value={editForm.unidadeId} onChange={(e) => setEditForm(p => ({ ...p, unidadeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground">
                  <option value="">Matriz</option>
                  {editUnidades.map((unidade) => (<option key={unidade.id} value={unidade.id}>{unidade.nome}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Data de Referência *</label>
                <input type="date" value={editForm.dataReferencia} onChange={(e) => setEditForm(p => ({ ...p, dataReferencia: e.target.value }))} required
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeEditModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
